import { Globe, TrendingUp } from "lucide-react";
import ScorecardInfoTip from "./ScorecardInfoTip";
import { fmtNum } from "./formatters";

/**
 * Panel de sub-fuentes Web — desglose de leads Web por marca / microsite.
 *
 * Rediseñado 2026-08-10: pasa de layout tabular denso a una grilla de
 * "site cards" (una tarjeta por marca) con volumen + conversión + barra
 * comparativa. Más rápido de escanear cuando hay 6-8 marcas y se ve
 * mejor tanto en escritorio como en mobile.
 */
export default function WebSubsourcesPanel({ data }) {
  const rows = data || [];
  if (!rows.length) return null;

  const total = rows.reduce((s, r) => s + (r.leads || 0), 0);
  const maxLeads = Math.max(...rows.map((r) => r.leads || 0), 1);
  const totalConv = rows.reduce((s, r) => s + (r.converted || 0), 0);
  const globalConv = total > 0 ? +((totalConv / total) * 100).toFixed(2) : 0;

  return (
    <div className="surface p-6" data-testid="web-subsources-panel">
      <div className="mb-4 flex items-start justify-between gap-3">
        <div>
          <h3 className="font-display text-base font-extrabold inline-flex items-center gap-1.5"
            style={{ letterSpacing: "-0.01em" }}>
            <Globe size={16} className="text-sky-600" />
            Fuentes Web · Desglose por Microsite
            <ScorecardInfoTip
              label="Fuentes Web"
              formula="Leads del canal Web agrupados por marca (proxy del microsite de origen)."
              extra="El webhook Zapier no captura la URL exacta. La marca resuelta por resolve_branch() es el mejor proxy: normalmente el formulario pertenece al microsite de esa marca."
              source="leads_digital.brand donde channel='Web'"
            />
          </h3>
          <p className="text-[11px] text-gray-500 mt-1">
            {fmtNum(total)} leads · conversión global{" "}
            <span className="font-bold" style={{ color: globalConv >= 5 ? "#166534" : globalConv >= 2 ? "#854d0e" : "#991b1b" }}>
              {globalConv}%
            </span>
          </p>
        </div>
      </div>

      <div
        className="grid gap-3"
        style={{ gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" }}>
        {rows.map((r) => {
          const pct = total > 0 ? (r.leads / total) * 100 : 0;
          const share = (r.leads / maxLeads) * 100;
          const convHi = r.conversion_pct >= 5;
          const convMid = r.conversion_pct >= 2 && r.conversion_pct < 5;
          const convColor = convHi ? "#16a34a" : convMid ? "#f59e0b" : "#dc2626";
          const convBg = convHi ? "#dcfce7" : convMid ? "#fef3c7" : "#fee2e2";
          return (
            <div
              key={r.brand}
              data-testid={`web-subsource-row-${r.brand.replace(/\s+/g, '-').toLowerCase()}`}
              className="rounded-lg p-3"
              style={{ border: "1px solid #e2e8f0", background: "#ffffff" }}>
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="min-w-0 flex-1">
                  <div className="font-bold text-[13px] text-gray-900 truncate" title={r.brand}>
                    {r.brand}
                  </div>
                  <div className="text-[10px] font-semibold text-gray-500 mt-0.5">
                    {pct.toFixed(1)}% del tráfico web
                  </div>
                </div>
                <div
                  className="text-[10px] font-extrabold tabular-nums px-2 py-0.5 rounded flex items-center gap-1"
                  style={{ background: convBg, color: convColor }}>
                  <TrendingUp size={10} />
                  {r.conversion_pct}%
                </div>
              </div>
              <div className="flex items-baseline gap-1.5 mb-2">
                <span className="text-2xl font-extrabold tabular-nums text-gray-900"
                  style={{ fontFamily: "Manrope, sans-serif", letterSpacing: "-0.02em", lineHeight: 1 }}>
                  {fmtNum(r.leads)}
                </span>
                <span className="text-[10px] font-semibold text-gray-500">
                  leads · {fmtNum(r.converted)} conv
                </span>
              </div>
              <div style={{ height: 6, background: "#f1f5f9", borderRadius: 999, overflow: "hidden" }}>
                <div
                  style={{
                    height: "100%",
                    width: `${Math.max(3, share)}%`,
                    background: convColor,
                    transition: "width 0.4s ease",
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-4 text-[10px] text-gray-400" style={{ lineHeight: 1.5 }}>
        Conversión ≥ 5% = verde · 2-5% = amarillo · &lt; 2% = rojo · barra = share vs. la marca de mayor tráfico.
      </div>
    </div>
  );
}
